<script setup lang="ts">
import type { SearchResult } from '@satisa-memory/shared'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, unwrap } from '../lib/api'
import { initials, shortDate } from '../lib/format'
import { KIND_LABELS, statusLabel, withWhom } from '../lib/ledger'
import {
  contextOf,
  type Hit,
  highlight,
  hitsOf,
  hitTo,
  nextScope,
  type Scope,
  SCOPES,
  SECTION_LABELS,
  type Section,
  sectionOrder,
  sectionTo,
  shortcutLabel
} from '../lib/palette'
import KindMark from './KindMark.vue'
import SourceBadge from './SourceBadge.vue'

/**
 * ⌘K (Ctrl K), on every page: one box over the library, the ledger and the
 * people (GET /api/search). The page it was opened on comes first — the
 * ledger's items on the Ledger page — and Tab narrows it to one section.
 * Empty, it offers the pages and the quick add. ↑↓ walk the results, ↵ opens
 * one, and each section ends with its own page searched for the same words.
 * The header's search button opens it too (`memory:search`).
 */

const route = useRoute()
const router = useRouter()
const dialog = ref<HTMLDialogElement | null>(null)
const input = ref<HTMLInputElement | null>(null)
const listEl = ref<HTMLElement | null>(null)
const q = ref('')
const scope = ref<Scope>('all')
const result = ref<SearchResult | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const active = ref(0)
const shortcut = shortcutLabel(navigator.platform || navigator.userAgent)

/** The page it was opened on, kept while it is open. */
const context = ref<Section | null>(null)

const PAGES = [
  { label: 'Library', to: '/' },
  { label: 'Calendar', to: '/calendar' },
  { label: 'Ledger', to: '/ledger' },
  { label: 'Decisions', to: '/ledger?view=decisions' },
  { label: 'People', to: '/people' },
  { label: 'Sync', to: '/sync' },
  { label: 'Devices', to: '/devices' }
]

type Row =
  | { type: 'hit'; hit: Hit }
  | { type: 'more'; section: Section }
  | { type: 'page'; label: string; to: string }
  | { type: 'add' }

const sections = computed(() => {
  if (!q.value.trim() || !result.value) return []
  const hits = hitsOf(result.value)
  return sectionOrder(context.value, scope.value)
    .map((s) => ({ section: s, hits: hits[s] }))
    .filter((s) => s.hits.length || scope.value !== 'all')
})

/** Every row the arrows walk, in order on screen. */
const rows = computed<Row[]>(() => {
  if (!q.value.trim()) {
    return [{ type: 'add' }, ...PAGES.map((p) => ({ type: 'page' as const, ...p }))]
  }
  return sections.value.flatMap((s) => [
    ...s.hits.map((hit) => ({ type: 'hit' as const, hit })),
    { type: 'more' as const, section: s.section }
  ])
})
const indexOf = (r: Row) => rows.value.indexOf(r)
const nothing = computed(
  () => !!q.value.trim() && !!result.value && !loading.value && !sections.value.length
)

// ---- opening and closing ----------------------------------------------------

function open() {
  if (dialog.value?.open) {
    input.value?.select()
    return
  }
  context.value = contextOf(route.path)
  scope.value = 'all'
  active.value = 0
  error.value = null
  dialog.value?.showModal()
  void nextTick(() => input.value?.select())
}

function close() {
  dialog.value?.close()
}

function go(r: Row | undefined) {
  if (!r) return
  close()
  if (r.type === 'hit') void router.push(hitTo(r.hit))
  else if (r.type === 'more') void router.push(sectionTo(r.section, q.value))
  else if (r.type === 'page') void router.push(r.to)
  else window.dispatchEvent(new CustomEvent('memory:quick-add'))
}

// ---- searching --------------------------------------------------------------

let generation = 0
let pause: ReturnType<typeof setTimeout> | undefined

async function search() {
  const words = q.value.trim()
  const mine = ++generation
  if (!words) {
    result.value = null
    loading.value = false
    return
  }
  loading.value = true
  try {
    const limit = scope.value === 'all' ? 5 : 15
    const r = await unwrap(api.api.search.$get({ query: { q: words, limit: String(limit) } }))
    if (mine !== generation) return
    result.value = r
    error.value = null
    active.value = 0
  } catch (e) {
    if (mine === generation) error.value = e instanceof Error ? e.message : 'Search failed'
  } finally {
    if (mine === generation) loading.value = false
  }
}

watch(q, () => {
  clearTimeout(pause)
  pause = setTimeout(() => void search(), 120)
})
watch(scope, () => void search())

// ---- keys -------------------------------------------------------------------

function onInputKey(e: KeyboardEvent) {
  const n = rows.value.length
  if (e.key === 'ArrowDown' && n) {
    active.value = (active.value + 1) % n
  } else if (e.key === 'ArrowUp' && n) {
    active.value = (active.value - 1 + n) % n
  } else if (e.key === 'Enter') {
    go(rows.value[active.value])
  } else if (e.key === 'Tab') {
    scope.value = nextScope(scope.value, e.shiftKey)
  } else return
  e.preventDefault()
}

watch(active, async (i) => {
  await nextTick()
  listEl.value?.querySelector(`[data-row="${i}"]`)?.scrollIntoView({ block: 'nearest' })
})

function onKey(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    if (dialog.value?.open) close()
    else open()
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener('memory:search', open)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('memory:search', open)
  clearTimeout(pause)
})
</script>

<template>
  <dialog ref="dialog" class="palette" aria-label="Search" @click.self="close">
    <div class="box">
      <div class="field">
        <svg class="glass" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" stroke-width="1.6" />
          <path
            d="M10.5 10.5 14 14"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
          />
        </svg>
        <input
          ref="input"
          v-model="q"
          class="q"
          type="text"
          :placeholder="scope === 'all' ? 'Search everything…' : `Search ${SECTION_LABELS[scope]}…`"
          aria-label="Search"
          autocomplete="off"
          spellcheck="false"
          role="combobox"
          aria-controls="palette-results"
          :aria-expanded="rows.length > 0"
          :aria-activedescendant="rows.length ? `palette-row-${active}` : undefined"
          @keydown="onInputKey"
        />
        <span v-if="loading" class="spinner" aria-hidden="true" />
        <span class="kbd">esc</span>
      </div>

      <div class="scopes" role="tablist" aria-label="Search in">
        <button
          v-for="s in SCOPES"
          :key="s"
          type="button"
          role="tab"
          class="scope"
          :class="{ on: scope === s }"
          :aria-selected="scope === s"
          tabindex="-1"
          @click="((scope = s), input?.focus())"
        >
          {{ s === 'all' ? 'Everything' : SECTION_LABELS[s] }}
          <span v-if="s === context && scope === 'all'" class="here" title="This page, first"
            >·here</span
          >
        </button>
        <span class="muted tab-hint"><span class="kbd">tab</span> to narrow</span>
      </div>

      <div id="palette-results" ref="listEl" class="results" role="listbox">
        <p v-if="error" class="state error">{{ error }}</p>
        <p v-else-if="nothing" class="state">
          Nothing matches “{{ q.trim() }}”{{
            scope === 'all' ? '' : ` in ${SECTION_LABELS[scope]}`
          }}.
        </p>

        <template v-if="!q.trim()">
          <p class="head eyebrow">Do</p>
          <button
            v-for="r in rows.filter((x) => x.type === 'add')"
            :id="`palette-row-${indexOf(r)}`"
            :key="'add'"
            type="button"
            role="option"
            class="row"
            :class="{ active: indexOf(r) === active }"
            :data-row="indexOf(r)"
            :aria-selected="indexOf(r) === active"
            @mousemove="active = indexOf(r)"
            @click="go(r)"
          >
            <span class="icon plus">+</span>
            <span class="main">Add a to-do or decision</span>
            <span class="kbd">n</span>
          </button>
          <p class="head eyebrow">Go to</p>
          <template v-for="r in rows" :key="r.type === 'page' ? r.to : 'x'">
            <button
              v-if="r.type === 'page'"
              :id="`palette-row-${indexOf(r)}`"
              type="button"
              role="option"
              class="row"
              :class="{ active: indexOf(r) === active }"
              :data-row="indexOf(r)"
              :aria-selected="indexOf(r) === active"
              @mousemove="active = indexOf(r)"
              @click="go(r)"
            >
              <span class="icon">→</span>
              <span class="main">{{ r.label }}</span>
            </button>
          </template>
        </template>

        <section v-for="s in sections" :key="s.section" class="section">
          <p class="head eyebrow">
            {{ SECTION_LABELS[s.section] }}
            <span v-if="s.section === context" class="here">this page</span>
          </p>
          <template v-for="r in rows" :key="r.type === 'hit' ? r.hit.id : `more-${s.section}`">
            <button
              v-if="r.type === 'hit' && r.hit.section === s.section"
              :id="`palette-row-${indexOf(r)}`"
              type="button"
              role="option"
              class="row"
              :class="{ active: indexOf(r) === active }"
              :data-row="indexOf(r)"
              :aria-selected="indexOf(r) === active"
              @mousemove="active = indexOf(r)"
              @click="go(r)"
            >
              <template v-if="r.hit.section === 'ledger'">
                <span class="icon"><KindMark :kind="r.hit.item.kind" /></span>
                <span class="main">
                  <span class="title" :class="{ closed: r.hit.item.status !== 'open' }"
                    ><template v-for="(run, k) in highlight(r.hit.item.text, q)" :key="k"
                      ><mark v-if="run.hit">{{ run.text }}</mark
                      ><template v-else>{{ run.text }}</template></template
                    ></span
                  >
                  <span class="sub muted">
                    {{ KIND_LABELS[r.hit.item.kind] }}
                    <template v-if="withWhom(r.hit.item).length">
                      ·
                      {{
                        withWhom(r.hit.item)
                          .map((p) => p.name)
                          .join(', ')
                      }}</template
                    >
                    <template v-if="r.hit.item.status !== 'open'">
                      · {{ statusLabel(r.hit.item) }}</template
                    >
                    · {{ r.hit.item.link.title }}
                  </span>
                </span>
                <span class="when muted">{{
                  shortDate(r.hit.item.date ?? r.hit.item.dueDate ?? r.hit.item.link.date)
                }}</span>
              </template>
              <template v-else-if="r.hit.section === 'library'">
                <span class="icon"
                  ><SourceBadge
                    :source="r.hit.link.source"
                    :kind="r.hit.link.kind"
                    :item-kind="r.hit.link.itemKind"
                    :size="16"
                /></span>
                <span class="main">
                  <span class="title"
                    ><template v-for="(run, k) in highlight(r.hit.link.title, q)" :key="k"
                      ><mark v-if="run.hit">{{ run.text }}</mark
                      ><template v-else>{{ run.text }}</template></template
                    ></span
                  >
                  <span v-if="r.hit.link.summary" class="sub muted">{{ r.hit.link.summary }}</span>
                </span>
                <span class="when muted">{{ shortDate(r.hit.link.lastSharedAt) }}</span>
              </template>
              <template v-else>
                <span class="icon avatar">{{ initials(r.hit.person.displayName) }}</span>
                <span class="main">
                  <span class="title"
                    ><template v-for="(run, k) in highlight(r.hit.person.displayName, q)" :key="k"
                      ><mark v-if="run.hit">{{ run.text }}</mark
                      ><template v-else>{{ run.text }}</template></template
                    ></span
                  >
                  <span class="sub muted"
                    >{{ r.hit.person.email ?? r.hit.person.organisation ?? '' }}
                    {{ r.hit.person.linkCount ? `· ${r.hit.person.linkCount} links` : '' }}</span
                  >
                </span>
              </template>
            </button>
            <button
              v-else-if="r.type === 'more' && r.section === s.section"
              :id="`palette-row-${indexOf(r)}`"
              type="button"
              role="option"
              class="row more"
              :class="{ active: indexOf(r) === active }"
              :data-row="indexOf(r)"
              :aria-selected="indexOf(r) === active"
              @mousemove="active = indexOf(r)"
              @click="go(r)"
            >
              <span class="icon">↗</span>
              <span class="main">Search {{ SECTION_LABELS[s.section] }} for “{{ q.trim() }}”</span>
            </button>
          </template>
        </section>
      </div>

      <footer class="foot muted">
        <span><span class="kbd">↑</span><span class="kbd">↓</span> move</span>
        <span><span class="kbd">↵</span> open</span>
        <span><span class="kbd">tab</span> narrow</span>
        <span class="right"
          ><span class="kbd">{{ shortcut }}</span> anywhere</span
        >
      </footer>
    </div>
  </dialog>
</template>

<style scoped>
.palette {
  width: min(640px, calc(100vw - 2rem));
  max-height: min(560px, calc(100dvh - 4rem));
  margin: 12vh auto auto;
  padding: 0;
  color: var(--fg);
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: calc(var(--radius) + 4px);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.palette[open] {
  animation: rise 0.14s ease-out;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.985);
  }
}

.palette::backdrop {
  background: rgba(10, 8, 4, 0.35);
  backdrop-filter: blur(2px);
}

.box {
  display: flex;
  flex-direction: column;
  max-height: inherit;
}

.field {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--line);
}

.glass {
  flex: none;
  width: 16px;
  height: 16px;
  color: var(--muted);
}

.q {
  flex: 1;
  min-width: 0;
  padding: 0;
  font: inherit;
  font-size: 16px;
  color: var(--fg);
  background: none;
  border: 0;
  outline: none;
}

.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid var(--accent-line);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.scopes {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--line);
}

.scope {
  padding: 0.2rem 0.6rem;
  font: inherit;
  font-size: 12.5px;
  color: var(--fg-2);
  background: var(--surface-2);
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
}

.scope.on {
  color: var(--accent);
  background: var(--accent-soft);
  border-color: var(--accent-line);
  font-weight: 600;
}

.here {
  margin-left: 0.15rem;
  font-size: 11px;
  font-weight: 500;
  color: var(--muted);
}

.tab-hint {
  margin-left: auto;
  font-size: 12px;
}

.results {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.35rem 0.4rem 0.5rem;
}

.head {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin: 0.6rem 0.6rem 0.25rem;
}

.head .here {
  text-transform: none;
  letter-spacing: 0;
}

.row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.45rem 0.6rem;
  font: inherit;
  text-align: left;
  color: var(--fg);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.row.active {
  background: var(--accent-soft);
}

.row.more.active {
  color: var(--accent);
}

.icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 24px;
  height: 24px;
  font-size: 13px;
  color: var(--muted);
}

.icon.plus {
  font-size: 16px;
  color: var(--accent);
}

.icon.avatar {
  font-size: 10.5px;
  font-weight: 650;
  color: var(--accent);
  background: var(--accent-soft);
  border-radius: 50%;
}

.main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.title {
  overflow: hidden;
  font-size: 14px;
  font-weight: 550;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.title.closed {
  color: var(--muted);
  text-decoration: line-through;
}

.title mark {
  color: inherit;
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  border-radius: 2px;
}

.sub {
  overflow: hidden;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.when {
  flex: none;
  font-size: 12px;
}

.more {
  font-size: 13px;
  color: var(--fg-2);
}

.state {
  margin: 1.25rem 1rem;
  font-size: 13.5px;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.foot {
  display: flex;
  gap: 0.9rem;
  padding: 0.5rem 1rem;
  font-size: 12px;
  border-top: 1px solid var(--line);
  background: var(--bg);
}

.foot .kbd + .kbd {
  margin-left: 0.15rem;
}

.foot .right {
  margin-left: auto;
}

@media (max-width: 600px) {
  .palette {
    width: 100vw;
    max-width: 100vw;
    max-height: calc(100dvh - 3rem);
    margin: 0 0 auto;
    border-radius: 0 0 var(--radius) var(--radius);
  }

  .foot,
  .tab-hint {
    display: none;
  }
}
</style>
